import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import edit from "../../../../assets/images/edit.png";
import { jqxCookieCall } from '../../../../Components/common/CommonFunctions';

const GenericJqxGrid = React.forwardRef(({
    tab = 'Active',
    apiUrl,
    datafields = [],
    columns = [],
    onRowEdit,
    onRowDelete,
    onRowSelect,
    onRowUnselect,
    onCellClick,
    onCustomClick,
    showActions = true,
    selectionMode = 'checkbox',
    height = 600,
    pageSize = 20,
    initialFilters = null,
    autoLoadFilters = true,
    idPrefix = 'grid',
    defaultPageSize = 50,
    rowsHeight = 42,
    columnsHeight = 45,
    filterRowHeight = 45
}, ref) => {
    const gridRef = useRef(null);
    const [isGridReady, setIsGridReady] = useState(false);

    useEffect(() => {
        if (gridRef.current && window.$ && window.$.jqx && !isGridReady) {
            const timer = setTimeout(() => {
                initializeGrid();
                setIsGridReady(true);
            }, 100);

            return () => clearTimeout(timer);
        }

        return () => {
            if (gridRef.current && window.$(gridRef.current).jqxGrid) {
                try {
                    window.$(gridRef.current).jqxGrid('destroy');
                } catch (e) {
                    console.log('Grid cleanup error:', e);
                }
            }
        };
    }, [tab, apiUrl, datafields, columns]);

    const initializeGrid = () => {
        if (!gridRef.current || !window.$ || !window.$.jqx) {
            console.error('Grid initialization failed: DOM or jQWidgets not ready');
            return;
        }

        if (!apiUrl) {
            console.error('Grid initialization failed: apiUrl is required');
            return;
        }

        if (!datafields || datafields.length === 0) {
            console.error('Grid initialization failed: datafields are required');
            return;
        }

        try {
            // const token = "Bearer " + localStorage.RFS;
            jqxCookieCall()

            const source = {
                datatype: 'json',
                datafields: datafields,
                id: `${idPrefix}-${tab}-`,
                url: apiUrl,
                // beforeSend: function (jqXHR) {
                //     jqXHR.setRequestHeader('Authorization', token);
                // },
                pagesize: defaultPageSize,
                filter: function () {
                    window.$(gridRef.current).jqxGrid('clearselection');
                    window.$(gridRef.current).jqxGrid("updatebounddata", "filter");
                },
                beforeprocessing: function (data) {
                    source.totalrecords = data.TotalRows;
                },
                sort: function () {
                    window.$(gridRef.current).jqxGrid("updatebounddata", "sort");
                },
                root: "Rows",
                cache: false,
            };

            const dataAdapter = new window.$.jqx.dataAdapter(source);

            // Build columns with optional actions column
            const gridColumns = [
                ...(showActions ? [{
                    text: 'Actions',
                    cellsrenderer: function () {
                        return '<div class="grid_actions"><img src="' + edit + '" class="row_edit" /></div>';
                    },
                    width: '4%',
                    filterable: false,
                }] : []),
                ...columns
            ];

            const gridOptions = {
                width: '100%',
                source: dataAdapter,
                columns: gridColumns,
                autoheight: false,
                height: height,
                sortable: true,
                altrows: false,
                columnsresize: false,
                enabletooltips: false,
                editable: false,
                selectionmode: selectionMode,
                pageable: true,
                pageSize: pageSize,
                pagesizeoptions: ['50', '100', '200', '500'],
                pagerButtonsCount: 5,
                pagerMode: 'advanced',
                filterable: true,
                filterrowheight: filterRowHeight,
                showfilterrow: true,
                rowsheight: rowsHeight,
                columnsheight: columnsHeight,
                showstatusbar: true,
                virtualmode: true,
                rendergridrows: function (params) {
                    return params.data;
                },
                ready: function () {
                    if (autoLoadFilters && initialFilters) {
                        applyFilters(initialFilters);
                    }
                }
            };

            window.$(gridRef.current).jqxGrid(gridOptions);
            window.$(gridRef.current).find('.jqx-grid-column-header').css('background-color', 'white');

            // Event handlers
            if (onRowEdit) {
                window.$(gridRef.current).on('click', '.row_edit', function () {
                    const rowData = getRowDataFromClick(this);
                    if (rowData) onRowEdit(rowData);
                });
            }

            if (onRowDelete) {
                window.$(gridRef.current).on('click', '.row_delete', function () {
                    const rowData = getRowDataFromClick(this);
                    if (rowData) onRowDelete(rowData);
                });
            }

            // Generic custom click handler for any custom cell classes
            if (onCustomClick) {
                console.log("1")
                Object.keys(onCustomClick).forEach(className => {
                    window.$(gridRef.current).on('click', `.${className}`, function () {
                        const rowData = getRowDataFromClick(this);
                        if (rowData) onCustomClick[className](rowData);
                    });
                });
            }

            if (onRowSelect) {
                console.log("2") 
                window.$(gridRef.current).on('rowselect', (event) => {
                    if (event.args.row) {
                        onRowSelect(event.args.row);
                    }
                });
            }

            if (onRowUnselect) {
                window.$(gridRef.current).on('rowunselect', (event) => {
                    if (event.args.row) {
                        onRowUnselect(event.args.row);
                    }
                });
            }

            if (onCellClick) {
                window.$(gridRef.current).on('cellclick', (event) => {
                    const args = event.args.row.bounddata;
                    if (event.args.columnindex > (showActions ? 1 : 0)) {
                        onCellClick(args);
                    }
                });
            }
        } catch (error) {
            console.error('Error initializing grid:', error);
        }
    };

    const getRowDataFromClick = (element) => {
        const rowDiv = window.$(element).closest('[role="row"]');
        const rowIndex = window.$(rowDiv).attr('row-id');
        const boundIndex = window.$(gridRef.current).jqxGrid('getrowid', rowIndex);
        
        if (boundIndex !== undefined) {
            const pagingInfo = window.$(gridRef.current).jqxGrid('getpaginginformation');
            const pageIndex = pagingInfo.pagenum;
            const pageSize = pagingInfo.pagesize;
            const globalIndex = parseInt(rowIndex) + pageIndex * pageSize;
            const rowData = window.$(gridRef.current).jqxGrid('getrowdata', globalIndex);
            return rowData;
        }
        return null;
    };

    const applyFilters = (filters) => {
        if (!filters || filters.length === 0) return;

        filters.forEach(element => {
            const filterValue = element.filters[0].value;
            if (filterValue !== null && filterValue !== undefined) {
                const filterGroup = new window.$.jqx.filter();
                const filterCondition = element.filters[0].condition ?? "CONTAINS";
                const filter = filterGroup.createfilter("stringfilter", filterValue, filterCondition);
                filterGroup.addfilter(1, filter);
                window.$(gridRef.current).jqxGrid("addfilter", element.datafield, filterGroup);
            }
        });
        window.$(gridRef.current).jqxGrid("applyfilters");
    };

    // Public methods
    const refresh = () => {
        if (gridRef.current) {
            window.$(gridRef.current).jqxGrid('updatebounddata');
        }
    };

    const clearSelection = () => {
        if (gridRef.current) {
            window.$(gridRef.current).jqxGrid('clearselection');
        }
    };

    const getSelectedRows = () => {
        if (gridRef.current) {
            return window.$(gridRef.current).jqxGrid('getselectedrowindexes');
        }
        return [];
    };

    const getRowData = (rowIndex) => {
        if (gridRef.current) {
            return window.$(gridRef.current).jqxGrid('getrowdata', rowIndex);
        }
        return null;
    };

    const addFilter = (datafield, filterGroup) => {
        if (gridRef.current) {
            window.$(gridRef.current).jqxGrid("addfilter", datafield, filterGroup);
        }
    };

    const applyFiltersMethod = () => {
        if (gridRef.current) {
            window.$(gridRef.current).jqxGrid("applyfilters");
        }
    };

    const clearFilters = () => {
        if (gridRef.current) {
            window.$(gridRef.current).jqxGrid("clearfilters");
        }
    };

    // Expose methods via ref
    React.useImperativeHandle(ref, () => ({
        refresh,
        clearSelection,
        getSelectedRows,
        getRowData,
        addFilter,
        applyFilters: applyFiltersMethod,
        clearFilters
    }));

    return <div ref={gridRef}></div>;
});

export default GenericJqxGrid;